import React, { useState, useEffect } from "react";

const FacilityModal = ({ isOpen, onClose, facility, onSuccess }) => {
  const isEditing = !!facility;

  const [facilityName, setFacilityName] = useState("");
  const [facilityType, setFacilityType] = useState("");
  const [customerWeighting, setCustomerWeighting] = useState("");
  const [facilityLogo, setFacilityLogo] = useState(null);
  const [attribution, setAttribution] = useState("");

  const getApiBaseUrl = () => process.env.REACT_APP_API_URL || "http://localhost:8000";

  // Pre-fill form when editing
  useEffect(() => {
    if (facility) {
      setFacilityName(facility.name || "");
      setFacilityType(facility.type || "");
      setCustomerWeighting(facility.customer_weighting || "");
      setAttribution(facility.attribution || "");
    }
  }, [facility]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    if (!facilityName || !facilityType || !customerWeighting || !attribution) {
      alert("All fields are required. Please fill them before submitting.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", facilityName);
      formData.append("type", facilityType);
      formData.append("customer_weighting", customerWeighting);
      if (facilityLogo) formData.append("logo", facilityLogo);
      else if (!isEditing) {
        alert("Logo is required for new facilities.");
        return;
      }
      formData.append("attribution", attribution);

      const url = isEditing
        ? `${getApiBaseUrl()}/api/facilities/facilities/${facility.id}/`
        : `${getApiBaseUrl()}/api/facilities/facilities/`;

      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        body: formData,
      });

      if (!response.ok)
        throw new Error(`Failed to ${isEditing ? "update" : "add"} facility`);

      alert(`Facility ${isEditing ? "updated" : "added"} successfully!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(`Error ${isEditing ? "updating" : "adding"} facility:`, error);
      alert(`Failed to ${isEditing ? "update" : "add"} facility. Please try again.`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {isEditing ? "Update Facility" : "Add Facility"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700">Facility Name</label>
            <input
              type="text"
              value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700">Facility Type</label>
            <input
              type="text"
              value={facilityType}
              onChange={(e) => setFacilityType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700">Customer Weighting</label>
            <input
              type="number"
              step="0.1"
              value={customerWeighting}
              onChange={(e) => setCustomerWeighting(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700">Facility Logo</label>
            <input
              type="file"
              onChange={(e) => setFacilityLogo(e.target.files[0])}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
            {facility?.logo && (
              <img
                src={facility.logo}
                alt="current logo"
                className="w-12 h-12 mt-2 object-contain border rounded"
              />
            )}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700">Attribution</label>
            <input
              type="text"
              value={attribution}
              onChange={(e) => setAttribution(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-500 text-white px-4 py-2 rounded-md mr-2 hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
              {isEditing ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FacilityModal;
